import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getOrderByNumber } from '../utils/api';
import { FiPackage, FiTruck, FiCheckCircle, FiXCircle, FiArrowLeft } from 'react-icons/fi';
import './TrackOrder.css';

const TrackOrder = () => {
  const [searchValue, setSearchValue] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);
    setOrder(null);

    try {
      const response = await getOrderByNumber(searchValue.trim().toUpperCase());
      if (response.success && response.order) {
        setOrder(response.order);
      } else {
        setError('Order not found. Please check the order number.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Order not found. Please check the order number.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'processing':
        return <FiPackage className="status-icon confirmed" />;
      case 'shipped':
      case 'out_for_delivery':
        return <FiTruck className="status-icon shipped" />;
      case 'delivered':
        return <FiCheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <FiXCircle className="status-icon cancelled" />;
      default:
        return <FiPackage className="status-icon" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Processing',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered',
      cancelled: 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get simplified timeline - only confirmed and delivery statuses
  const getSimplifiedTimeline = (statusHistory, orderStatus) => {
    if (!statusHistory || statusHistory.length === 0) return [];
    
    const timeline = [];
    
    // Find confirmed status
    const confirmed = statusHistory.find(s => s.status === 'confirmed');
    if (confirmed) {
      timeline.push({
        status: 'Order Confirmed',
        timestamp: confirmed.timestamp,
        icon: <FiCheckCircle className="timeline-icon confirmed" />
      });
    }
    
    // Find shipped status
    const shipped = statusHistory.find(s => s.status === 'shipped');
    if (shipped) {
      timeline.push({
        status: 'Shipped',
        timestamp: shipped.timestamp,
        icon: <FiTruck className="timeline-icon shipped" />
      });
    }
    
    // Find out for delivery status
    const outForDelivery = statusHistory.find(s => s.status === 'out_for_delivery');
    if (outForDelivery) {
      timeline.push({
        status: 'Out for Delivery',
        timestamp: outForDelivery.timestamp,
        icon: <FiTruck className="timeline-icon shipped" />
      });
    }
    
    // Find delivered status
    const delivered = statusHistory.find(s => s.status === 'delivered');
    if (delivered) {
      timeline.push({
        status: 'Delivered',
        timestamp: delivered.timestamp,
        icon: <FiCheckCircle className="timeline-icon delivered" />
      });
    }
    
    // Find cancelled status
    const cancelled = statusHistory.find(s => s.status === 'cancelled');
    if (cancelled) {
      timeline.push({
        status: 'Cancelled',
        timestamp: cancelled.timestamp,
        icon: <FiXCircle className="timeline-icon cancelled" />
      });
    }
    
    return timeline;
  };

  const renderOrderCard = (order) => {
    const timeline = getSimplifiedTimeline(order.statusHistory, order.orderStatus);
    
    return (
      <div className="order-card">
        <div className="order-card-header">
          <div className="order-info">
            <span className="order-number">Order #{order.orderNumber}</span>
            <span className="order-date">Placed on {formatDate(order.createdAt)}</span>
          </div>
          <div className={`order-status ${order.orderStatus}`}>
            {getStatusIcon(order.orderStatus)}
            <span>{getStatusText(order.orderStatus)}</span>
          </div>
        </div>

        <div className="order-items">
          {order.items.map((item, index) => (
            <div key={index} className="order-item">
              <div className="item-details">
                <p className="item-name">{item.productName}</p>
                <div className="item-meta">
                  {item.variantWeight && (
                    <span className="item-variant">{item.variantWeight}</span>
                  )}
                  <span className="item-qty">Qty: {item.quantity}</span>
                </div>
              </div>
              <span className="item-price">₹{(item.price * item.quantity).toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="order-card-footer">
          <div className="order-total">
            <span>Total Amount</span>
            <span className="total-amount">₹{order.totalAmount.toLocaleString()}</span>
          </div>
        </div>

        {timeline.length > 0 && (
          <div className="status-timeline">
            <h4>Order Status</h4>
            <div className="timeline">
              {timeline.map((item, index) => (
                <div key={index} className={`timeline-item ${index === timeline.length - 1 ? 'current' : ''}`}>
                  <div className="timeline-dot">
                    {item.icon}
                  </div>
                  <div className="timeline-content">
                    <span className="timeline-status">{item.status}</span>
                    <span className="timeline-date">{formatDate(item.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {order.orderStatus === 'delivered' && (
          <div className="delivery-complete">
            <FiCheckCircle />
            <span>Order delivered successfully</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="track-order-page">
      <div className="container">
        <Link to="/" className="back-link">
          <FiArrowLeft /> Back to Home
        </Link>

        <div className="track-order-header">
          <h1>Track Your Order</h1>
          <p>Enter your order number to check delivery status</p>
        </div>

        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Enter order number (e.g., PK2602201234)"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary search-btn" disabled={loading}>
              {loading ? 'Searching...' : 'Track Order'}
            </button>
          </form>
        </div>

        {error && (
          <div className="error-message">
            <FiXCircle />
            <span>{error}</span>
          </div>
        )}

        {searched && !loading && !error && order && (
          <div className="results-section">
            <div className="single-order-result">
              {renderOrderCard(order)}
            </div>
          </div>
        )}

        {!searched && (
          <div className="track-info">
            <div className="info-card">
              <FiPackage className="info-icon" />
              <h3>Order Number</h3>
              <p>Your order number starts with "PK" and was shown after placing your order</p>
            </div>
            <div className="info-card">
              <FiTruck className="info-icon" />
              <h3>Fast Delivery</h3>
              <p>Fast delivery to your doorstep</p>
            </div>
            <div className="info-card">
              <FiCheckCircle className="info-icon" />
              <h3>Track Anytime</h3>
              <p>Check your order status anytime using your order number</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
